/* eslint-disable better-mutation/no-mutation */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { hideAllErrors } from './syi';
import type {
    ItemDraftImage,
    TemplateImage
} from 'tradera/state/services/types/webapi-selling-generated';
import config from 'tradera/apps/syi/services/config';

// This will be increased when supporting more images.
export const MAX_NUMBER_OF_IMAGES = config.isSyiTenImagesEnabled ? 10 : 5;
export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

export enum ITEM_CLASSIFICATIONS_FOR_PREDICTION {
    Books = 'books',
    Cameras = 'cameras',
    Clothes = 'clothes',
    DigitalMedia = 'digital_media',
    // Other means that we cannot do predictions for this category
    Other = 'other',
    Shoes = 'shoes',
    Stamps = 'stamps',
    Watches = 'watches'
}

type ImagesSlice = {
    images: Image[];
    uploadingCount: number;
    validationError: string | null;
    uploadError: string | null;
    isUploadingWithApp: boolean;
};

export type Rotation = 0 | 90 | 180 | 270;
type DraftImage = {
    imageId: string;
    isUploadedBySystem: boolean;
    imageUrl: string;
    rotationDegrees: Rotation;
    isListingImage: false;
};
type ListingImage = {
    imageId: number;
    imageGroupId: number;
    filename: string;
    isUploadedBySystem: boolean;
    imageUrl: string;
    rotationDegrees: Rotation;
    isListingImage: true;
};
type OtherImage = {
    imageId: string;
    imageUrl: string;
    isListingImage: false;
    rotationDegrees: Rotation;
    isUploadedBySystem: false;
};
export type Image = DraftImage | ListingImage | OtherImage;

const initialState: ImagesSlice = {
    images: [],
    uploadingCount: 0,
    validationError: null,
    uploadError: null,
    isUploadingWithApp: false
};

export function setValidation(state: ImagesSlice) {
    if (state.uploadingCount > 0) {
        state.validationError = 'syi_validation_images_uploading';
    } else if (state.images.length === 0) {
        state.validationError = 'syi_validation_images';
    } else if (state.images.every((image) => image.isUploadedBySystem)) {
        // If there isn't at least one image uploaded by the user (can be uploaded by Klarna for example)
        state.validationError = 'syi_validation_images_not_uploaded_by_user';
    } else {
        state.validationError = null;
    }
}

function resetUploadError(state: ImagesSlice) {
    state.uploadError = null;
}

const imagesSlice = createSlice({
    name: 'images',
    initialState,
    reducers: {
        setDraftImages: (
            state,
            action: PayloadAction<ItemDraftImage[] | TemplateImage[]>
        ) => {
            state.images = action.payload.map((image) => {
                if ('draftImage' in image && image.draftImage) {
                    return {
                        imageUrl: image.imageUrl,
                        imageId: image.draftImage.imageId,
                        isUploadedBySystem: image.draftImage.isUploadedBySystem,
                        rotationDegrees: image.draftImage.rotationDegrees,
                        isListingImage: false
                    } as DraftImage;
                }
                if ('listingImage' in image && image.listingImage) {
                    return {
                        imageUrl: image.imageUrl,
                        imageId: image.listingImage.imageId,
                        imageGroupId: image.listingImage.imageGroupId,
                        filename: image.listingImage.filename,
                        isUploadedBySystem:
                            image.listingImage.isUploadedBySystem,
                        rotationDegrees: image.listingImage.rotationDegrees,
                        isListingImage: true
                    } as ListingImage;
                }
                if ('imageId' in image && image.imageId) {
                    return {
                        imageUrl: image.imageUrl,
                        imageId: image.imageId,
                        isUploadedBySystem: false,
                        rotationDegrees: image.rotationDegrees,
                        isListingImage: false
                    } as OtherImage;
                }
                throw Error('Unknown image type');
            });
            resetUploadError(state);
            setValidation(state);
        },
        addImage: (
            state,
            action: PayloadAction<{
                imageUrl: string;
                imageId: string;
            }>
        ) => {
            if (state.images.length >= MAX_NUMBER_OF_IMAGES) {
                return;
            }

            const { imageId, imageUrl } = action.payload;
            state.images.push({
                imageId,
                imageUrl,
                isListingImage: false,
                isUploadedBySystem: false,
                rotationDegrees: 0
            });
            if (state.uploadingCount > 0) {
                state.uploadingCount--;
            }
            resetUploadError(state);
            setValidation(state);
        },
        replaceImage: (
            state,
            action: PayloadAction<{
                index: number;
                imageUrl: string;
                imageId: string;
            }>
        ) => {
            const { imageId, imageUrl, index } = action.payload;

            if (!state.images[index]) {
                throw Error('Replace image failed');
            }
            state.images[index] = {
                imageId,
                imageUrl,
                isListingImage: false,
                isUploadedBySystem: false,
                rotationDegrees: 0
            };
            resetUploadError(state);
            setValidation(state);
        },
        removeImage: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            if (!state.images[index]) {
                throw new Error('Remove failed, invalid position.');
            }

            state.images.splice(index, 1);
            resetUploadError(state);
            setValidation(state);
        },
        changeImagePosition: (
            state,
            action: PayloadAction<{ from: number; to: number }>
        ) => {
            const { from, to } = action.payload;
            if (!state.images[from] || !state.images[to]) {
                throw new Error(
                    'Change image position failed, invalid position.'
                );
            }

            const movedImage = state.images.splice(from, 1);
            state.images.splice(to, 0, movedImage[0]);
            resetUploadError(state);
            setValidation(state);
        },
        setImageRotation: (
            state,
            action: PayloadAction<{
                index: number;
                rotationDegrees: Rotation;
            }>
        ) => {
            const { index, rotationDegrees } = action.payload;
            if (!state.images[index]) {
                throw new Error('Set rotation failed, invalid position.');
            }

            state.images[index].rotationDegrees = rotationDegrees;
            resetUploadError(state);
            setValidation(state);
        },
        setImageUploadFailed: (
            state,
            action: PayloadAction<{ errorMessage: string }>
        ) => {
            const { errorMessage } = action.payload;
            if (state.uploadingCount > 0) {
                state.uploadingCount--;
            }
            state.uploadError = errorMessage;
            setValidation(state);
        },
        setImagesUploading: (
            state,
            action: PayloadAction<{ numberOfImages: number }>
        ) => {
            const { numberOfImages } = action.payload;
            state.uploadingCount = numberOfImages;
            resetUploadError(state);
            setValidation(state);
        },
        setMobileUpload: (
            state,
            action: PayloadAction<{ isUploadingWithApp: boolean }>
        ) => {
            const { isUploadingWithApp } = action.payload;
            state.isUploadingWithApp = isUploadingWithApp;

            if (action.payload.isUploadingWithApp) {
                resetUploadError(state);
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(hideAllErrors.type, (state) => {
            state.uploadError = null;
        });
    }
});

export default imagesSlice.reducer;

export const {
    setDraftImages,
    addImage,
    replaceImage,
    removeImage,
    changeImagePosition,
    setImageRotation,
    setImageUploadFailed,
    setImagesUploading,
    setMobileUpload
} = imagesSlice.actions;
